import EventBus from '@mvps-genesis/event-bus';
import ParameterValidator from '@mvps-genesis/parameter-validator';
import MicroModal from 'micromodal';

/*
 *--------------------------------------------------------------------------
 * MiniBasket
 *--------------------------------------------------------------------------
 *
 * Class to handle mink basket functionality and interaction.
 *
 */
export default class MiniBasket {

	/**
	 * sessionStorage key used to flag that the mini basket should be
	 * re-opened on the next page load (used as a fallback if a quantity form
	 * ever has to submit natively, or a remove link has to be followed
	 * natively, instead of going via AJAX).
	 *
	 * @var String
	 */
	static REOPEN_STORAGE_KEY = 'MVPS:reopenMiniBasket';

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	_validator;

	/**
	 * Modal object
	 *
	 * @var Object
	 */
	modal;

	/**
	 * Element selectors object
	 *
	 * @var Object
	 */
	elementSelectors;

	/**
	 * Mini basket container element
	 *
	 * @var Element
	 */
	containerElement;

	/**
	 * Mini basket content element
	 *
	 * @var Element
	 */
	contentElement;

	/**
	 * List of elements that open the mini basket and contain the mini basket trigger attribute
	 *
	 * @var NodeList
	 */
	openElements;

	/**
	 * Attribute to trigger mini basket open
	 *
	 * @var String
	 */
	openAttribute;

	/**
	 * List of basket count elements
	 *
	 * @var NodeList
	 */
	countElements;

	/**
	 * List of subtotal elements
	 *
	 * @var NodeList
	 */
	subtotalElements;

	/**
	 * Open mini basket on instantiation
	 *
	 * @var Boolean
	 */
	isOpenByDefault;

	/**
	 * Open mini basket after successfully adding product to cart
	 *
	 * @var Boolean
	 */
	openOnAddToCart;

	/**
	 * Close mini basket after opening on successful add to cart
	 *
	 * @var Boolean
	 */
	closeAfterOpen;

	/**
	 * Close mini basket delay time (ms)
	 *
	 * @var Integer
	 */
	closeAfterOpenTime;

	/**
	 * Mini basket has items class name
	 *
	 * @var String
	 */
	hasItemsClass;

	/**
	 * Tracks in-flight update requests, keyed by Basket_Group id, so rapid
	 * repeated actions on the same line item are serialized: quantity
	 * submissions coalesce so the final requested quantity always wins
	 * instead of racing, a removal clicked mid-update runs once that update
	 * lands, and once a removal is pending any further quantity change or
	 * repeat removal for that group is dropped.
	 *
	 * @var Map
	 */
	pendingGroupUpdates;

	/**
	 * Tail of the queue every basket update request runs through. Each response
	 * re-renders the whole drawer, so updates for different line items must not
	 * overlap, or a slower, older response could land last and roll it back.
	 *
	 * @var Promise
	 */
	basketUpdateQueue;

	/**
	 * Set once a fallback has started a native submit/navigation. Updates still
	 * queued for other line items are skipped from then on, so one of them
	 * can't fire its own fallback and cancel the navigation already under way.
	 *
	 * @var Boolean
	 */
	navigatingAway;

	/**
	 * Forms currently mid-dispatch of a 'submit' event this tick, used to
	 * swallow the duplicate 'submit' event that fires when a user presses
	 * Enter in a quantity field: the change handler's own requestSubmit()
	 * call and the browser's separate native implicit-submission-on-Enter
	 * behavior both dispatch a distinct 'submit' event for the same
	 * keypress. Cleared via microtask so unrelated, later submissions are
	 * never blocked.
	 *
	 * @var WeakSet
	 */
	formsHandlingSubmit;

	/**
	 * Create a new MiniBasket object
	 *
	 * @param	Boolean		closeAfterOpen		Close mini basket after opened via openOnAddToCart. Defaults to false.
	 * @param	Integer		closeAfterOpenTime	Close mini basket after delay (ms) if closeAfterOpen option is true. Defaults to 5000.
	 * @param	Element		containerElement	Root container element to target for mini basket functionality. Defaults to '.js-mini-basket'.
	 * @param	Element		contentElement		Element containing mini basket main content. Defaults to '.g-mini-basket__content'.
	 * @param	ElementList	countElements		List of basket count elements. Defaults to '.js-mini-basket-count'.
	 * @param	String		hasItemsClass		Class to apply to mini basket when items are in the basket. Defaults to 'g-site-header__basket-has-items'.
	 * @param	Boolean		isOpenByDefault		Open mini basket on class instantiation. Defaults to false.
	 * @param	String		openAttribute		Attribute name to trigger mini basket open. This should be placed on the openElements. Defaults to 'data-modal-trigger'.
	 * @param	ElementList	openElements		List of elements that open the mini basket and contain the mini basket trigger attribute. Defaults to '.js-mini-basket-open'.
	 * @param	Boolean		openOnAddToCart		Listens for the global `MVPS:addedToCart` event and opens if successful. Defaults to false.
	 * @param	ElementList	subtotalElements	List of subtotal elements. Defaults to '.js-mini-basket-subtotal'.
	 */
	// eslint-disable-next-line max-lines-per-function
	constructor ({
		closeAfterOpen = false,
		closeAfterOpenTime = 5000,
		containerElement = '.js-mini-basket',
		contentElement = '.g-mini-basket__content',
		countElements = '.js-mini-basket-count',
		hasItemsClass = 'g-site-header__basket-has-items',
		isOpenByDefault = false,
		openAttribute = 'data-modal-trigger',
		openElements = '.js-mini-basket-open',
		openOnAddToCart = false,
		subtotalElements = '.js-mini-basket-subtotal'
	} = {}) {
		// Create validator
		this._validator = new ParameterValidator(this);

		// Save selectors
		this.elementSelectors = {
			containerElement,
			contentElement,
			countElements,
			openElements,
			subtotalElements
		};

		// Create properties from options
		this.closeAfterOpenTime = this._validator.isInt('options.closeAfterOpenTime', closeAfterOpenTime);
		this.closeAfterOpen = this._validator.isBoolean('options.closeAfterOpen', closeAfterOpen);
		this.containerElement = this._validator.isElement('options.containerElement', containerElement);
		this.contentElement = this._validator.isElement('options.contentElement', contentElement);
		this.countElements = this._validator.isElementList('options.countElements', countElements);
		this.hasItemsClass = this._validator.isString('options.hasItemsClass', hasItemsClass);
		this.isOpenByDefault = this._validator.isBoolean('options.isOpenByDefault', isOpenByDefault);
		this.openAttribute = this._validator.isString('options.openAttribute', openAttribute);
		this.openElements = this._validator.isElementList('options.openElements', openElements);
		this.openOnAddToCart = this._validator.isBoolean('options.openOnAddToCart', openOnAddToCart);
		this.subtotalElements = this._validator.isElementList('options.subtotalElements', subtotalElements);

		// Determine if user want reduced motion. If they do, no animations.
		const awaitAnimation = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

		// Set modal object
		this.modal = {};
		this.modal.id = this.containerElement.id || 'js-mini-basket';
		this.modal.options = {
			awaitCloseAnimation: awaitAnimation,
			awaitOpenAnimation: awaitAnimation,
			disableScroll: true,
			openTrigger: this.openAttribute
		};

		// Track in-flight group-update requests and duplicate submit dispatches
		this.pendingGroupUpdates = new Map();
		this.basketUpdateQueue = Promise.resolve();
		this.navigatingAway = false;
		this.formsHandlingSubmit = new WeakSet();

		// Run bind events init function
		this.bindEvents();

		// Run open command if isOpenByDefault is true, or if an update had to fall back to a native submit/navigation on the previous page
		if (this.isOpenByDefault || this.consumeReopenFlag()) {
			this.open();
		} else {
			MicroModal.init(this.modal.options);
		}

		this.initQuantify();
	}

	/**
	 * Check for, and clear, the sessionStorage flag set by flagReopen() when an update has to fall back to a native submit or navigation.
	 *
	 * @return Boolean True if the mini basket should be re-opened on this page load.
	 */
	consumeReopenFlag () {
		try {
			const shouldReopen = Boolean(sessionStorage.getItem(MiniBasket.REOPEN_STORAGE_KEY));

			sessionStorage.removeItem(MiniBasket.REOPEN_STORAGE_KEY);

			return shouldReopen;
		} catch {
			// sessionStorage can throw just from being accessed (e.g. Safari with all
			// cookies/site data blocked) - treat that the same as "nothing to reopen"
			// rather than letting it abort the rest of Global.onReady().
			return false;
		}
	}

	async initQuantify () {
		const forms = this.contentElement.getElementsByClassName('js-mini-basket-form-update');

		if (!forms.length) {
			return;
		}

		const {Quantify} = await import('extensions/quantify');

		for (const form of forms) {
			// Guard against attaching duplicate listeners if initQuantify() runs more than once against the same DOM (e.g. re-triggered before a content replacement occurs)
			if (form.dataset.quantifyInitialized) {
				continue;
			}

			form.dataset.quantifyInitialized = 'true';

			// Intercept the form's actual submit event so every submission trigger is caught -- not just
			// the increment/decrement buttons, but also a <select> quantity field's native implicit
			// submission (e.g. pressing Enter after choosing an option), which never fires via 'change'
			// interception alone since it's a real browser-initiated submit, not a JS .submit() call.
			form.addEventListener('submit', (event) => {
				event.preventDefault();

				// Pressing Enter in the quantity field fires this listener twice for one
				// keypress: once from the change handler's requestSubmit() call below, once
				// from the browser's own native implicit submission. Swallow the second one
				// within the same tick; later, unrelated submissions are never blocked since
				// this clears again on the next microtask.
				if (this.formsHandlingSubmit.has(form)) {
					return;
				}

				this.formsHandlingSubmit.add(form);
				Promise.resolve().then(() => this.formsHandlingSubmit.delete(form));

				this.submitQuantityForm(form);
			});

			new Quantify({
				formElement: form,
				// Route button-driven changes through requestSubmit() so they fire a real 'submit' event
				// too, caught by the listener above instead of bypassing it via a plain .submit() call.
				onSubmit: (formElement) => formElement.requestSubmit(),
				wrapper: form.querySelector('[data-hook="quantify"]')
			});
		}
	}

	/**
	 * Submit a mini-basket quantity update form via AJAX instead of a native
	 * submit, so the page does not reload and the mini basket stays open.
	 *
	 * Serializes repeated submissions for the same Basket_Group: if a request
	 * for that group is already in flight, this captures the FormData now and
	 * lets the in-flight request's loop pick it up once it resolves, so rapid
	 * clicks always end up submitting the last-requested quantity instead of
	 * racing (whichever fetch happens to resolve last would otherwise win).
	 *
	 * @param  Element formElement The mini-basket quantity update form element.
	 */
	async submitQuantityForm (formElement) {
		const formData = new FormData(formElement);
		const groupId = formData.get('Basket_Group');
		const pending = this.pendingGroupUpdates.get(groupId);

		if (pending) {
			// Don't queue behind a removal - that line item is about to disappear,
			// so the quantity it would have been set to is moot.
			if (!pending.removal) {
				pending.nextFormData = formData;
			}

			return;
		}

		const state = {nextFormData: null, removal: null};

		this.pendingGroupUpdates.set(groupId, state);

		// finally, so a thrown update can't leave this group locked until the next page load
		try {
			let currentFormData = formData;

			do {
				const submittedFormData = currentFormData;

				// eslint-disable-next-line no-await-in-loop
				const applied = await this.performBasketUpdate(
					new Request(formElement.action, {
						body: submittedFormData,
						method: formElement.method || 'POST'
					}),
					// A removal queued meanwhile makes this quantity moot, so fall back to the removal instead of dropping it.
					// Otherwise submit the latest quantity asked for, not the one this request carried.
					() => (state.removal
						? this.fallbackToNavigation(state.removal.url)
						: this.fallbackToNativeSubmit(formElement, state.nextFormData || submittedFormData))
				);

				// The fallback is already navigating away, so anything queued behind it must not fire too.
				if (!applied) {
					return;
				}

				currentFormData = state.nextFormData;
				state.nextFormData = null;
			} while (currentFormData);

			if (state.removal) {
				await this.performRemoval(state.removal);
			}
		} finally {
			this.pendingGroupUpdates.delete(groupId);
		}
	}

	/**
	 * Remove a line item from the basket via AJAX instead of following its
	 * remove link natively, so the page does not reload and the mini basket
	 * stays open.
	 *
	 * A removal clicked while a quantity change for the same group is in
	 * flight runs once that change lands; a repeat removal click is dropped.
	 *
	 * @param  Element linkElement  The mini-basket remove link element.
	 * @param  Boolean restoreFocus Move focus back into the drawer afterwards (keyboard activation).
	 */
	async removeGroup (linkElement, restoreFocus = false) {
		// data-basket-group is the raw Basket_Group id, deliberately matching what
		// submitQuantityForm() keys on, so a removal and a quantity change for the
		// same line item land on the same map entry and can exclude each other.
		const groupId = linkElement.dataset.basketGroup;
		const removeLinks = Array.from(this.contentElement.querySelectorAll('.js-mini-basket-remove'));
		const index = removeLinks.indexOf(linkElement);
		const nextLink = removeLinks[index + 1];
		const removal = {
			index,
			nextGroupId: nextLink ? nextLink.dataset.basketGroup : null,
			restoreFocus,
			url: linkElement.href
		};
		const pending = this.pendingGroupUpdates.get(groupId);

		if (pending) {
			if (!pending.removal) {
				pending.removal = removal;
				pending.nextFormData = null;
			}

			return;
		}

		this.pendingGroupUpdates.set(groupId, {nextFormData: null, removal});

		try {
			await this.performRemoval(removal);
		} finally {
			this.pendingGroupUpdates.delete(groupId);
		}
	}

	/**
	 * Send a single line item removal and apply its result to the drawer.
	 *
	 * @param  Object  removal              The removal to perform.
	 * @param  Number  removal.index        Position of the clicked link among the drawer's remove links.
	 * @param  String  removal.nextGroupId  Basket_Group of the line item below the clicked one, if any.
	 * @param  String  removal.url          The remove link's URL.
	 * @param  Boolean removal.restoreFocus Move focus back into the drawer afterwards.
	 */
	async performRemoval ({index, nextGroupId, restoreFocus, url}) {
		// no-store: this GET changes the basket, so it must never be answered from cache
		const applied = await this.performBasketUpdate(
			new Request(url, {cache: 'no-store'}),
			() => this.fallbackToNavigation(url)
		);

		// The activated link was destroyed by the content swap, which drops keyboard focus out of the dialog.
		if (applied && restoreFocus && !this.contentElement.contains(document.activeElement)) {
			const remainingLinks = Array.from(this.contentElement.querySelectorAll('.js-mini-basket-remove'));

			// Land on the item that took the removed one's place, not back at the top of the list. Matched
			// by group rather than index alone, since other removals queued ahead may have shifted positions.
			const focusTarget = remainingLinks.find((link) => link.dataset.basketGroup === nextGroupId)
				|| remainingLinks[Math.min(index, remainingLinks.length - 1)]
				|| this.contentElement.querySelector('[data-micromodal-close]');

			if (focusTarget) {
				focusTarget.focus();
			}
		}
	}

	/**
	 * Perform a single mini-basket update request -- a quantity change or a
	 * line item removal -- and apply its result to the drawer, queued behind
	 * any update already in flight for any line item (see basketUpdateQueue). Calls
	 * onFailure (flagging the mini basket to re-open on the next page load)
	 * if the request fails or the response is unusable, so the caller can
	 * fall back to its own plain non-AJAX behavior. Also notifies other
	 * page-specific listeners (e.g. the basket page's own product
	 * table/order summary) via EventBus, since this AJAX update only ever
	 * touches the mini-basket's own drawer content.
	 *
	 * @param  Request  request   The request to send for this update.
	 * @param  Function onFailure Invoked when the update could not be applied over AJAX.
	 * @return Boolean  True if the update was applied, false if it fell back to onFailure.
	 */
	performBasketUpdate (request, onFailure) {
		const update = this.basketUpdateQueue.then(() => this.sendBasketUpdate(request, onFailure));

		// Keep the queue moving even if one update throws
		this.basketUpdateQueue = update.catch(() => {});

		return update;
	}

	/**
	 * Send one queued basket update and apply its response; see performBasketUpdate().
	 *
	 * @param  Request  request   The request to send for this update.
	 * @param  Function onFailure Invoked when the update could not be applied over AJAX.
	 * @return Boolean  True if the update was applied, false if it fell back to onFailure.
	 */
	async sendBasketUpdate (request, onFailure) {
		if (this.navigatingAway) {
			return false;
		}

		let responseDom;

		try {
			const response = await fetch(request);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			responseDom = new DOMParser().parseFromString(await response.text(), 'text/html');
		} catch {
			onFailure();

			return false;
		}

		const newContainer = this.getContainerElementFromPayload({data: responseDom});

		if (!newContainer) {
			onFailure();

			return false;
		}

		this.applyContainer(newContainer);

		// Let other page-specific listeners (e.g. the basket page's own table/order
		// summary) refresh themselves from the same parsed response - no second fetch.
		EventBus.dispatchEvent('MVPS:miniBasketQuantityUpdated', {data: responseDom});

		return true;
	}

	/**
	 * Flag the mini basket to re-open on the next page load, so a fallback that
	 * navigates away doesn't leave the drawer shut behind the user's back, and
	 * stop any still-queued updates from firing (see navigatingAway).
	 */
	flagReopen () {
		this.navigatingAway = true;

		try {
			sessionStorage.setItem(MiniBasket.REOPEN_STORAGE_KEY, '1');
		} catch {
			// Best-effort only - if sessionStorage is blocked, the mini basket just won't reopen on the next page load.
		}
	}

	/**
	 * Flag the mini basket to re-open on the next page load, then submit the form data natively.
	 *
	 * Submits a fresh copy rather than formElement itself: once replaceContents()
	 * has swapped the drawer, formElement is detached, and a detached form won't
	 * submit at all.
	 *
	 * @param  Element  formElement The form whose action/method to submit to.
	 * @param  FormData formData    The data to submit.
	 */
	fallbackToNativeSubmit (formElement, formData) {
		const form = document.createElement('form');

		form.action = formElement.action;
		form.method = formElement.method;
		form.hidden = true;

		for (const [name, value] of formData) {
			const input = document.createElement('input');

			input.type = 'hidden';
			input.name = name;
			input.value = value;
			form.append(input);
		}

		document.body.append(form);

		this.flagReopen();

		form.submit();
	}

	/**
	 * Flag the mini basket to re-open on the next page load, then follow the link natively.
	 *
	 * @param  String url The URL to navigate to.
	 */
	fallbackToNavigation (url) {
		this.flagReopen();

		window.location.assign(url);
	}

	/**
	 * Bind the add-to-cart, remove link and open trigger listeners.
	 */
	bindEvents () {
		// Listen for global event - `MVTPS:addToCart` and handle response
		EventBus.addEventListener('MVPS:addToCart', this.onAddToCart.bind(this), false);

		// Coming Back from a fallback navigation can restore this page from bfcache with navigatingAway still set
		window.addEventListener('pageshow', (event) => {
			if (event.persisted) {
				this.navigatingAway = false;
			}
		});

		// Delegated from the content element rather than bound per link, so removal keeps
		// working after replaceContents() swaps out the drawer's innerHTML on every update.
		this.contentElement.addEventListener('click', (event) => {
			const removeLink = event.target.closest('.js-mini-basket-remove');

			if (!removeLink) {
				return;
			}

			event.preventDefault();

			// detail is 0 for a keyboard-activated click, which is when focus needs restoring
			this.removeGroup(removeLink, event.detail === 0);
		});

		for (const element of this.openElements) {
			element.addEventListener('click', (event) => {
				event.preventDefault();
			});
		}
	}

	onAddToCart (e) {
		const payload = e.detail;

		if (payload.success) {
			this.onAddToCartSuccess(payload);
		}
		else {
			this.onAddToCartError(payload);
		}
	};

	onAddToCartSuccess (payload) {
		const newContainer = this.getContainerElementFromPayload(payload);

		if (!newContainer) {
			return;
		}

		this.applyContainer(newContainer);

		// Open mini basket if configured
		if (this.openOnAddToCart) {
			setTimeout(() => {
				this.open();

				// Close mini basket after X ms if configured
				if (this.closeAfterOpen) {
					setTimeout(() => {
						MicroModal.close(this.modal.id);
					}, this.closeAfterOpenTime);
				}
			}, 200);
		}
	}

	onAddToCartError () {
		return false;
	}

	/**
	 * Render a freshly fetched mini basket container into the drawer and header.
	 *
	 * @param  Element newContainer The mini basket container parsed from a response.
	 */
	applyContainer (newContainer) {
		this.replaceContents(newContainer);

		// Re-initialize Quantify for the freshly-rendered quantity forms
		this.initQuantify();

		this.count = this.getCount(newContainer);
		this.subtotal = this.getSubtotal(newContainer);

		this.updateCounts();
		this.updateSubtotals();
	}

	getContainerElementFromPayload (payload) {
		return payload.data.querySelector(this.elementSelectors.containerElement);
	}

	/**
	 * Swap the drawer's content and count/subtotal for those of a freshly rendered container.
	 *
	 * @param  Element containerElement The mini basket container parsed from a response.
	 */
	replaceContents (containerElement) {
		this._validator.isInstance('replaceContents.containerElement', containerElement, HTMLElement);

		const contentElement = containerElement.querySelector(this.elementSelectors.contentElement);

		if (!contentElement) {
			return;
		}

		this.contentElement.innerHTML = contentElement.innerHTML;

		// Only the inner content is swapped, so keep the container's own count/subtotal (read by getCount()/getSubtotal()) in step
		this.containerElement.dataset.count = containerElement.dataset.count || '';
		this.containerElement.dataset.subtotal = containerElement.dataset.subtotal || '';
	}

	replaceContainerElement (newContainer) {
		this.containerElement.parentNode.replaceChild(newContainer, this.containerElement);

		this.containerElement = newContainer;
	}

	getCount (containerElement) {
		const element = containerElement ? containerElement : this.containerElement;

		return element.dataset.count;
	}

	getSubtotal (containerElement) {
		const element = containerElement ? containerElement : this.containerElement;

		return element.dataset.subtotal;
	}

	updateCounts () {
		const count = parseInt(this.count, 10) || 0;

		for (const element of this.countElements) {
			element.textContent = this.count;

			// Toggle visibility to match the server-rendered state (see navigation_menu.mvt / cssui-global-header.mvt)
			element.classList.toggle('u-hidden', count === 0);
			element.classList.toggle('u-inline-flex', count > 0);
			element.classList.toggle('circle-large', count > 999);
		}

		for (const openElement of this.openElements) {
			openElement.classList.toggle(this.hasItemsClass, count > 0);
		}
	}

	updateSubtotals () {
		for (const element of this.subtotalElements) {
			element.textContent = this.subtotal;
		}
	}

	open () {
		MicroModal.show(this.modal.id, this.modal.options);
	}

};
